import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_precision-build-25/artifacts/umhdh1t2_Screenshot%202026-06-14%20at%2010.52.34%E2%80%AFPM.png";

const links = [
  { id: "home", key: "home" },
  { id: "services", key: "services" },
  { id: "projects", key: "projects" },
  { id: "about", key: "about" },
  { id: "contact", key: "contact" },
];

const Navbar = () => {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // On hero (scrolled=false), navbar is over dark image → light text
  const linkColor = scrolled ? "text-[#0F0F0F]/75 hover:text-[#B08D57]" : "text-white/85 hover:text-[#B08D57]";
  const langActive = scrolled ? "text-[#0F0F0F]" : "text-white";
  const langInactive = scrolled ? "text-[#0F0F0F]/40 hover:text-[#0F0F0F]/70" : "text-white/50 hover:text-white/80";
  const ctaBg = scrolled ? "bg-[#0F0F0F] text-white hover:bg-[#B08D57] hover:text-[#0F0F0F]" : "bg-[#B08D57] text-[#0F0F0F] hover:bg-white";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-light" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 h-24 flex items-center justify-between">
        <button
          data-testid="logo-link"
          onClick={() => goTo("home")}
          className="flex items-center group"
          aria-label="MITROLLARI ENGINEERING SOLUTIONS — Home"
        >
          <img
            src={LOGO_URL}
            alt="MITROLLARI ENGINEERING SOLUTIONS"
            className="h-16 w-auto object-contain group-hover:opacity-80 transition-opacity"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goTo(l.id)}
              className={`text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${linkColor}`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div data-testid="lang-switcher" className="hidden sm:flex items-center text-[11px] tracking-[0.2em] font-semibold">
            <button
              data-testid="lang-switch-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors ${lang === "en" ? langActive : langInactive}`}
            >EN</button>
            <span className={scrolled ? "text-[#0F0F0F]/20" : "text-white/25"}>/</span>
            <button
              data-testid="lang-switch-el"
              onClick={() => setLang("el")}
              className={`px-2 py-1 transition-colors ${lang === "el" ? langActive : langInactive}`}
            >EL</button>
          </div>
          <button
            data-testid="nav-cta-quote"
            onClick={() => goTo("contact")}
            className={`hidden md:inline-flex items-center px-6 py-3 text-[11px] tracking-[0.25em] uppercase font-semibold transition-colors ${ctaBg}`}
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden p-2 ${scrolled ? "text-[#0F0F0F]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#F7F5F2] border-t border-[#0F0F0F]/10">
          <nav className="px-6 py-8 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => goTo(l.id)}
                className="text-left text-[#0F0F0F] text-sm tracking-[0.12em] uppercase font-semibold hover:text-[#B08D57]"
              >{t.nav[l.key]}</button>
            ))}
            <div className="flex items-center gap-4 pt-4 border-t border-[#0F0F0F]/10 text-[11px] tracking-[0.2em] font-semibold">
              <button onClick={() => setLang("en")} data-testid="mobile-lang-en" className={lang === "en" ? "text-[#0F0F0F]" : "text-[#0F0F0F]/40"}>EN</button>
              <button onClick={() => setLang("el")} data-testid="mobile-lang-el" className={lang === "el" ? "text-[#0F0F0F]" : "text-[#0F0F0F]/40"}>EL</button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
